// Angular
import {
  Component,
  inject,
  OnDestroy,
  OnInit,
  ChangeDetectionStrategy,
} from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';

// RxJS
import { distinctUntilChanged, Subject, takeUntil } from 'rxjs';

// Component Library
import { FsxOption, FsxSelectComponent } from 'fsx-component-library';

// Local
import { ClerkReviewFilterFormService } from '@feature/crl/clerk-review-filter-form.service';
import { ClerkReviewFilterPayloadService } from '@feature/crl/clerk-review-filter-payload.service';
import { ClerkReviewFilterStoreValuesService } from '@feature/crl/clerk-review-filter-store-values.service';

/**
 * A more descriptive constant name to use in place of false option value.
 */
export const MAIN_DOCS_ONLY = false;

/**
 *  A more descriptive constant name to use in place of the true option value.
 */
export const MAIN_AND_SUPPORTING = true;

/**
 * The value to use as the default value here and in the test file.
 */
export const DEFAULT_DOCUMENTS_VALUE = MAIN_AND_SUPPORTING;

@Component({
  selector: 'app-document-filter-control',
  standalone: true,
  imports: [ReactiveFormsModule, FsxSelectComponent, FormsModule],
  templateUrl: './document-filter-control.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  styleUrls: ['./document-filter-control.component.scss'],
})
export class DocumentFilterControlComponent implements OnInit, OnDestroy {
  /*********************************************************************************************************************
   * Service Injections
   ********************************************************************************************************************/
  private readonly formService = inject(ClerkReviewFilterFormService);
  private readonly payloadService = inject(ClerkReviewFilterPayloadService);
  private readonly storeValuesService = inject(
    ClerkReviewFilterStoreValuesService,
  );

  /**
   * The value of Option that should be selected by default.
   */
  defaultValue = DEFAULT_DOCUMENTS_VALUE;

  /**
   * The FormControl for the "Documents" dropdown.
   */
  formControl = this.formService.documentsFormControl;

  /**
   * The collection of dropdown Option objects to pass to the FsxSelectComponent.
   */
  documentOptions: FsxOption<boolean>[] = [
    { value: MAIN_DOCS_ONLY, label: 'Main Documents Only' },
    { value: MAIN_AND_SUPPORTING, label: 'Main & Supporting' },
  ];

  /**
   * The Subject used to teardown the filter control listener subscriptions.
   */
  private destroy$$ = new Subject<void>();

  /**
   * The Angular lifecycle hook where we can safely run any code dependent on @Input() bindings.
   */
  ngOnInit(): void {
    // Get the stored value from the local storage.
    const storedValue = this.storeValuesService.getStoredDocumentValue();
    this.setValue(storedValue);
    this.startListeningForChanges();
  }

  /**
   * The Angular lifecycle hook for tearing down the subscriptions with the component.
   */
  ngOnDestroy(): void {
    this.destroy$$.next();
    this.destroy$$.complete();
  }

  /**
   * A private method to set up the FormControl valueChanges() listener.
   */
  private startListeningForChanges(): void {
    this.formControl.valueChanges
      .pipe(takeUntil(this.destroy$$), distinctUntilChanged())
      .subscribe((value: boolean) => {
        // Use the emitted value to set the Document filter payload.
        this.payloadService.setDocumentsFilter(value);
      });
  }

  /**
   * A public method to allow a new FormControl value to be set.
   * - If no value is provided then it falls back to the default value.
   *
   * @param {boolean | null} newValue - The new value to set.
   */
  setValue(newValue: boolean | null = null): void {
    const selectedValue = newValue !== null ? newValue : this.defaultValue; // Use provided value or fallback to default
    this.formControl.setValue(selectedValue); // Update FormControl with the selected value
  }
}
